/**
 * Help System Content Registry
 *
 * Central registry for all help articles, types, and utilities.
 */
// Import all help content modules
import { gettingStartedArticles } from './features/getting-started.js';
import { keyboardShortcutsArticles } from './features/keyboard-shortcuts.js';
import { calculationsArticles } from './features/calculations.js';
import { dashboardArticles } from './pages/dashboard.js';
import { jobsArticles } from './pages/jobs.js';
import { projectsArticles } from './pages/projects.js';
import { boqArticles } from './pages/boq.js';
import { salesArticles } from './pages/sales.js';
import { catalogueArticles } from './pages/catalogue.js';
import { ordersArticles } from './pages/orders.js';
import { suppliersArticles } from './pages/suppliers.js';
import { contactsArticles } from './pages/contacts.js';
import { accountingArticles } from './pages/accounting.js';
import { documentsArticles } from './pages/documents.js';
import { transmittalsArticles } from './pages/transmittals.js';
import { reportsArticles } from './pages/reports.js';
import { recycleBinArticles } from './pages/recycle-bin.js';
import { settingsArticles } from './pages/settings.js';
import { onboardingArticles } from './pages/onboarding.js';
import { rfqArticles } from './pages/rfq.js';
import { rfiArticles } from './pages/rfis.js';
import { quotesArticles } from './pages/quotes.js';
import { dailyLogsArticles } from './pages/daily-logs.js';
import { timesheetsArticles } from './pages/timesheets.js';
import { adminArticles } from './pages/admin.js';
import { reportDesignerArticles } from './pages/report-designer.js';
import { mobileArticles } from './features/mobile.js';
import { assistantArticles } from './features/assistant.js';
// Build the help registry
export const helpRegistry = {};
// Register all articles
const allArticles = [
    ...gettingStartedArticles,
    ...keyboardShortcutsArticles,
    ...calculationsArticles,
    ...mobileArticles,
    ...assistantArticles,
    ...dashboardArticles,
    ...jobsArticles,
    ...projectsArticles,
    ...boqArticles,
    ...catalogueArticles,
    ...salesArticles,
    ...quotesArticles,
    ...ordersArticles,
    ...rfqArticles,
    ...rfiArticles,
    ...dailyLogsArticles,
    ...timesheetsArticles,
    ...suppliersArticles,
    ...contactsArticles,
    ...accountingArticles,
    ...documentsArticles,
    ...transmittalsArticles,
    ...reportsArticles,
    ...recycleBinArticles,
    ...settingsArticles,
    ...onboardingArticles,
    ...adminArticles,
    ...reportDesignerArticles,
];
// Populate the registry
/** Article ids registered more than once — surfaced by tests/dev tooling. */
export const duplicateHelpArticleIds = [];
for (const article of allArticles) {
    if (helpRegistry[article.id]) {
        duplicateHelpArticleIds.push(article.id);
    }
    helpRegistry[article.id] = article;
}
/**
 * Get an article by ID
 */
export function getHelpArticle(id) {
    return helpRegistry[id] || null;
}
/**
 * Get all articles in a category
 */
export function getArticlesByCategory(category) {
    return Object.values(helpRegistry).filter(a => a.category === category);
}
/**
 * Get all category names
 */
export function getAllCategories() {
    const categories = new Set();
    for (const article of Object.values(helpRegistry)) {
        categories.add(article.category);
    }
    return Array.from(categories);
}
function snippetAround(text, term, radius = 140) {
    const idx = text.toLowerCase().indexOf(term);
    if (idx < 0)
        return undefined;
    const start = Math.max(0, idx - radius / 2);
    const raw = text.slice(start, start + radius).replace(/\s+/g, ' ').trim();
    return `${start > 0 ? '…' : ''}${raw}…`;
}
/** Score articles against a query (title > keywords > summary > body). */
export function searchHelpArticles(query, limit = 5) {
    const terms = query.toLowerCase().split(/\s+/).filter(t => t.length > 1);
    if (!terms.length)
        return [];
    const scored = [];
    for (const article of Object.values(helpRegistry)) {
        const title = article.title.toLowerCase();
        const keywords = (article.keywords ?? []).join(' ').toLowerCase();
        const summary = article.summary.toLowerCase();
        const body = [
            article.content,
            ...(article.expandableSections ?? []).map(s => `${s.title}\n${s.content}`),
        ].join('\n');
        const bodyLower = body.toLowerCase();
        let score = 0;
        let firstBodyTerm;
        for (const term of terms) {
            if (title.includes(term))
                score += 10;
            if (keywords.includes(term))
                score += 6;
            if (summary.includes(term))
                score += 4;
            if (bodyLower.includes(term)) {
                score += 2;
                firstBodyTerm ??= term;
            }
        }
        // Require more than an incidental single body hit for multi-term queries.
        if (score === 0 || (terms.length > 1 && score <= 2))
            continue;
        scored.push({
            score,
            result: {
                id: article.id,
                title: article.title,
                category: article.category,
                summary: article.summary,
                snippet: firstBodyTerm ? snippetAround(body, firstBodyTerm) : undefined,
            },
        });
    }
    return scored
        .sort((a, b) => b.score - a.score)
        .slice(0, Math.min(Math.max(limit, 1), 10))
        .map(s => s.result);
}
